import React, { useEffect, useRef, useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { BASE_URL } from "../../api/axios";
import AppBarCommon from "../AppBarCommon";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ErrorIcon from "@mui/icons-material/Error";
import useIntersectionObserver from "../../hooks/useIntersectioObserver";
import MenuIcon from "@mui/icons-material/Menu";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ViewEventsTable from "../common/Tables/ViewEventsTable";
import { EVENT_COLORS } from "../../constants.js";
import "./ViewEventsModal.css";
import dayjs from "dayjs";
import { useOutletContext } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const ImageViewComponent = ({
  side1_image,
  side2_image,
  is_side1: controlledSide1,
  onSwitchSide,
}) => {
  const [internalSide1, setInternalSide1] = useState(true);
  const is_side1 = controlledSide1 !== undefined ? controlledSide1 : internalSide1;
  const [loadingState, setLoadingState] = useState({ side1: true, side2: true });

  const hasSide2 = Boolean(side2_image);

  const switchSideImage = () => {
    if (hasSide2) {
      if (onSwitchSide) {
        onSwitchSide(!is_side1);
      } else {
        setInternalSide1((prev) => !prev);
      }
    }
  };

  const side1Url = `${BASE_URL}/defect-inspection/v1/${side1_image}?mode=low-res`;
  const side2Url = side2_image ? `${BASE_URL}/defect-inspection/v1/${side2_image}?mode=low-res` : null;

  const handleLoadError1 = () => setLoadingState(prev => ({ ...prev, side1: false }));
  const handleLoadError2 = () => setLoadingState(prev => ({ ...prev, side2: false }));

  const currentLoading = is_side1 ? loadingState.side1 : loadingState.side2;

  const sharedImgStyle = {
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    height: "62vh",
    objectFit: "contain",
    background: "black",
    width: "100%",
  };

  return (
    <div
      style={{
        position: "relative",
        borderTopLeftRadius: "10px",
        borderTopRightRadius: "10px",
        height: "62vh",
        width: "100%",
      }}
    >
      <img
        src={side1Url}
        alt="Side 1"
        onLoad={handleLoadError1}
        onError={handleLoadError1}
        style={{
          ...sharedImgStyle,
          display: is_side1 ? "block" : "none",
        }}
      />
      {hasSide2 && (
        <img
          src={side2Url}
          alt="Side 2"
          onLoad={handleLoadError2}
          onError={handleLoadError2}
          style={{
            ...sharedImgStyle,
            display: !is_side1 ? "block" : "none",
          }}
        />
      )}

      {hasSide2 && (
        <div className="image-slider-button" onClick={switchSideImage}>
          <div
            style={{
              width: "2rem",
              height: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {currentLoading ? (
              <CircularProgress size={24} style={{ color: "#fff", margin: "auto" }} />
            ) : (
              <NavigateNextIcon
                style={{ fontSize: "2rem", color: "#fff", margin: "auto" }}
              />
            )}
          </div>
          <span
            className="mt-3"
            style={{ color: "white", marginTop: "0.5rem", cursor: "pointer" }}
          >
            {is_side1 ? "side 2" : "side 1"}
          </span>
        </div>
      )}
    </div>
  );
};

const PredictionTableView = ({
  tableData,
  error_threshold,
  error_warn_threshold,
}) => {
  // const [isSide1, setIsSide1] = useState(true);
  // const [currentSideResult, setCurrentSlideResult] = useState(null);
  if (!error_warn_threshold) {
    error_warn_threshold = error_threshold;
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Orientation</th>
            <th>CAD</th>
            <th>SYS</th>
            <th>Difference (+/-)</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((rowData, index) => (
            <tr key={index}>
              <td>{rowData.name}</td>
              <td>{rowData.orientation}</td>
              <td>{rowData.length.toFixed(3)}</td>
              <td>{rowData.predicted_length.toFixed(3)}</td>
              <td>
                {error_threshold ? (
                  error_warn_threshold &&
                  error_warn_threshold >
                    Math.abs(rowData.length - rowData.predicted_length).toFixed(
                      3,
                    ) ? (
                    <span
                      style={{
                        display: "inline",
                        backgroundColor: EVENT_COLORS.SUCCESS_SECONDARY_COLOR,
                        color: EVENT_COLORS.SUCCESS_COLOR,
                        borderRadius: "10px",
                        padding: "5px 10px",
                        textAlign: "center",
                      }}
                    >
                      {(
                        rowData.predicted_length.toFixed(3) - rowData.length
                      ).toFixed(3)}
                    </span>
                  ) : error_threshold >
                    Math.abs(rowData.length - rowData.predicted_length).toFixed(
                      3,
                    ) ? (
                    <span
                      style={{
                        display: "inline",
                        backgroundColor: EVENT_COLORS.WARN_SECONDARY_COLOR,
                        color: EVENT_COLORS.WARN_COLOR,
                        borderRadius: "10px",
                        padding: "5px 10px",
                        textAlign: "center",
                      }}
                    >
                      {(
                        rowData.predicted_length.toFixed(3) - rowData.length
                      ).toFixed(3)}
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline",
                        color: EVENT_COLORS.DEFECT_COLOR,
                        backgroundColor: EVENT_COLORS.DEFECT_SECONDARY_COLOR,
                        borderRadius: "10px",
                        padding: "5px 10px",
                        textAlign: "center",
                      }}
                    >
                      {(
                        rowData.predicted_length.toFixed(3) - rowData.length
                      ).toFixed(3)}
                    </span>
                  )
                ) : (
                  <span
                    style={{
                      display: "inline",
                      backgroundColor: "#e9e9e9",
                      borderRadius: "10px",
                      padding: "5px 10px",
                      textAlign: "center",
                    }}
                  >
                    {(rowData.predicted_length - rowData.length).toFixed(3)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const formatDateTime = (stringValue, includeTimezone = false) => {
  if (!stringValue) return "";
  let cleanStr = stringValue.trim();
  // If not ending in Z or an offset like +00:00 / -05:00, specify UTC
  if (!cleanStr.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(cleanStr)) {
    cleanStr += " UTC";
  }
  const dateObj = new Date(cleanStr);
  const targetDate = isNaN(dateObj.getTime()) ? new Date(stringValue) : dateObj;
  if (isNaN(targetDate.getTime())) return stringValue;

  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  if (includeTimezone) {
    options.timeZoneName = "short";
  }
  return targetDate.toLocaleString("en-US", options);
};

const EventAccordionHeader = ({ event, formatDateTime }) => {
  const containerRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const dateTextRef = useRef(null);
  const dateTextWidthRef = useRef(140);
  const [isCompact, setIsCompact] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipToggle = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setTooltipOpen((prev) => !prev);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  useEffect(() => {
    if (!isCompact && dateTextRef.current) {
      const w = dateTextRef.current.offsetWidth;
      if (w > 50) {
        dateTextWidthRef.current = w;
      }
    }
  }, [isCompact]);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !leftRef.current || !rightRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const leftChildren = leftRef.current.children;
      if (!leftChildren || leftChildren.length === 0) return;

      let totalLeftWidth = 0;
      for (let i = 0; i < leftChildren.length; i++) {
        totalLeftWidth += leftChildren[i].offsetWidth;
      }
      totalLeftWidth += (leftChildren.length - 1) * 10; // 10px columnGap

      const rightWidth = rightRef.current.offsetWidth;
      const minSpacing = 16; // spacing between chips and right actions

      if (!isCompact) {
        if (totalLeftWidth + minSpacing + rightWidth > containerWidth) {
          setIsCompact(true);
        }
      } else {
        const neededWidth =
          totalLeftWidth +
          minSpacing +
          rightWidth +
          (dateTextWidthRef.current || 140);
        if (containerWidth >= neededWidth + 10) {
          setIsCompact(false);
        }
      }
    };

    checkOverflow();

    const observer = new ResizeObserver(() => {
      checkOverflow();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isCompact, event]);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div
        ref={leftRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          rowGap: "6px",
          columnGap: "10px",
          alignItems: "center",
        }}
      >
        <p
          style={{
            fontWeight: 500,
            color: "#115061",
            fontSize: "clamp(16px, 2.5vw, 24px)",
            margin: 0,
          }}
        >{`#${event?.edge_event_id}`}</p>
        <span
          style={{
            background: "#ebedeb",
            fontSize: "14px",
            fontWeight: "500",
            padding: "calc(0.25rem * 1) calc(0.25rem * 3)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          PID: {`${event?.workpiece_id}`}
        </span>
        <span
          style={{
            background: "#ebedeb",
            fontSize: "14px",
            fontWeight: "500",
            padding: "calc(0.25rem * 1) calc(0.25rem * 3)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          Order: {`${event?.order_number}`}
        </span>
        <span
          style={{
            background: "#ebedeb",
            fontSize: "14px",
            fontWeight: "500",
            padding: "calc(0.25rem * 1) calc(0.25rem * 3)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          Profile: {`${event?.profile_info?.profile}`}
        </span>
        <span
          style={{
            background: "#ebedeb",
            fontSize: "14px",
            fontWeight: "500",
            padding: "calc(0.25rem * 1) calc(0.25rem * 3)",
            borderRadius: "12px",
            textTransform: "capitalize",
            display: "flex",
            alignItems: "center",
          }}
        >
          Device: {`${event?.device_display_name || event?.device_name}`}
        </span>
      </div>
      <div
        ref={rightRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          marginLeft: "12px",
          flexShrink: 0,
        }}
      >
        {event?.created_date && (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <div>
              <Tooltip
                title={`Date & Time: ${formatDateTime(event?.created_date, true)}`}
                arrow
                placement="top"
                open={tooltipOpen}
                onClose={handleTooltipClose}
                onOpen={() => setTooltipOpen(true)}
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                disableFocusListener
              >
                <div
                  className={`event-datetime-badge ${isCompact ? "compact" : ""}`}
                  onClick={handleTooltipToggle}
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    zIndex: 5,
                  }}
                >
                  <AccessTimeIcon style={{ fontSize: "16px", pointerEvents: "none" }} />
                  <span
                    ref={dateTextRef}
                    className="event-datetime-text"
                    style={{
                      display: isCompact ? "none" : "inline",
                      pointerEvents: "none",
                    }}
                  >
                    {formatDateTime(event?.created_date)}
                  </span>
                </div>
              </Tooltip>
            </div>
          </ClickAwayListener>
        )}
        <p style={{ fontWeight: 500, margin: 0, whiteSpace: "nowrap" }}>view details</p>
      </div>
    </div>
  );
};

const EventCardItem = ({
  event,
  isLastItem,
  lastItemRef,
  getDefectiveColor,
  formatDateTime,
}) => {
  const [isSide1, setIsSide1] = useState(true);

  const side1_tableData = event?.side1_prediction?.measurements;
  const side1_error_threshold = event?.side1_prediction?.threshold;
  const side1_error_warn_threshold = event?.side1_prediction?.threshold_warn;
  const side2_tableData = event?.side2_prediction?.measurements;
  const side2_error_threshold = event?.side2_prediction?.threshold;
  const side2_error_warn_threshold = event?.side2_prediction?.threshold_warn;

  // Determine current side model status
  const isSideDefective = isSide1
    ? (event?.side1_prediction?.is_defective ?? (event?.status === "defective"))
    : (event?.side2_prediction?.is_defective ?? (event?.status === "defective"));
  const modelStatus = isSideDefective ? "REJECT" : "PASS";

  // Determine operator override for this side
  const feedbackData = event?.operator_feedback || event?.additional_info?.operator_feedback;
  const sideFeedback = isSide1
    ? feedbackData?.side1
    : feedbackData?.side2;

  // An override is only active if it differs from the model's status for this side
  const activeOverride =
    sideFeedback && sideFeedback.feedback_status !== modelStatus
      ? sideFeedback
      : null;

  return (
    <div
      ref={isLastItem ? lastItemRef : null}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "stretch",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        margin: "0 auto",
        boxSizing: "border-box",
      }}
    >
      <ImageViewComponent
        side1_image={event?.side1_annotated_image}
        side2_image={event?.side2_annotated_image}
        is_side1={isSide1}
        onSwitchSide={setIsSide1}
      />

      {/* Top-Left Badge: Model Predicted Status */}
      <div
        style={{
          position: "absolute",
          top: "1rem",
          zIndex: 10,
          left: "1rem",
          display: "flex",
          alignItems: "center",
        }}
      >
        {event?.status === "processing" ? (
          <div
            style={{
              color: "#000000ff",
              backgroundColor: "#f9f9f9ff",
              display: "inline-flex",
              alignItems: "center",
              height: "36px",
              boxSizing: "border-box",
              padding: "0 14px",
              gap: "8px",
              borderRadius: "20px",
              minWidth: "100px",
            }}
          >
            <ErrorIcon style={{ color: "#a3a3a3ff", fontSize: "20px" }} />
            <p
              style={{
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "Work Sans",
                textTransform: "capitalize",
                margin: 0,
                lineHeight: 1,
              }}
            >
              Processing
            </p>
          </div>
        ) : (
          <div
            style={{
              color: isSideDefective
                ? getDefectiveColor(event)
                : EVENT_COLORS.SUCCESS_COLOR,
              backgroundColor: isSideDefective
                ? getDefectiveColor(event, true)
                : EVENT_COLORS.SUCCESS_SECONDARY_COLOR,
              display: "inline-flex",
              alignItems: "center",
              height: "36px",
              boxSizing: "border-box",
              padding: "0 14px",
              gap: "8px",
              borderRadius: "20px",
              minWidth: "100px",
            }}
          >
            {isSideDefective ? (
              <ErrorIcon
                style={{
                  color: getDefectiveColor(event),
                  fontSize: "20px",
                }}
              />
            ) : (
              <CheckCircleIcon
                style={{
                  color: EVENT_COLORS.SUCCESS_COLOR,
                  fontSize: "20px",
                }}
              />
            )}
            <p
              style={{
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "Work Sans",
                textTransform: "capitalize",
                margin: 0,
                lineHeight: 1,
              }}
            >
              {modelStatus}
            </p>
          </div>
        )}
      </div>

      {/* Top-Right Badge: Operator Override (Read-Only) */}
      {activeOverride && (
        <div
          style={{
            position: "absolute",
            top: "1rem",
            zIndex: 10,
            right: "1rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              height: "36px",
              boxSizing: "border-box",
              padding: "0 14px",
              borderRadius: "20px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Work Sans",
              lineHeight: 1,
              backgroundColor:
                activeOverride.feedback_status === "PASS"
                  ? EVENT_COLORS.SUCCESS_SECONDARY_COLOR
                  : EVENT_COLORS.DEFECT_SECONDARY_COLOR,
              color:
                activeOverride.feedback_status === "PASS"
                  ? EVENT_COLORS.SUCCESS_COLOR
                  : EVENT_COLORS.DEFECT_COLOR,
              border: `1.5px solid ${
                activeOverride.feedback_status === "PASS"
                  ? EVENT_COLORS.SUCCESS_COLOR
                  : EVENT_COLORS.DEFECT_COLOR
              }`,
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
              cursor: "default",
              userSelect: "none",
            }}
          >
            {activeOverride.feedback_status === "PASS" ? (
              <CheckCircleIcon
                style={{
                  fontSize: "20px",
                  color: EVENT_COLORS.SUCCESS_COLOR,
                }}
              />
            ) : (
              <ErrorIcon
                style={{
                  fontSize: "20px",
                  color: EVENT_COLORS.DEFECT_COLOR,
                }}
              />
            )}
            <span>Operator: {activeOverride.feedback_status}</span>
          </div>
        </div>
      )}

      <div
        style={{
          width: "100%",
          marginBottom: "10px",
        }}
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
            sx={{
              userSelect: "text",
            }}
          >
            <EventAccordionHeader
              event={event}
              formatDateTime={formatDateTime}
            />
          </AccordionSummary>
          <AccordionDetails>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {side1_tableData && (
                <div style={{ margin: "14px", flex: "1 1 300px" }}>
                  <p
                    style={{
                      marginBottom: "10px",
                      fontSize: "1.2rem",
                      fontWeight: "600",
                    }}
                  >
                    Side 1
                  </p>
                  <PredictionTableView
                    tableData={side1_tableData}
                    error_threshold={side1_error_threshold}
                    error_warn_threshold={side1_error_warn_threshold}
                  />
                </div>
              )}
              {side2_tableData && (
                <div style={{ margin: "14px", flex: "1 1 300px" }}>
                  <p
                    style={{
                      marginBottom: "10px",
                      fontSize: "1.2rem",
                      fontWeight: "600",
                    }}
                  >
                    Side 2
                  </p>
                  <PredictionTableView
                    tableData={side2_tableData}
                    error_threshold={side2_error_threshold}
                    error_warn_threshold={side2_error_warn_threshold}
                  />
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

const ViewEventsModal = (props) => {
  const {drawerWidth} = useOutletContext();
  const { isHomePage, hasAdminRole } = props;
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState([]);
  const [apiEventsData, setAPIEventsData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isListView, setIsListView] = useState(false);
  const [isTableView, setIsTableView] = useState(false);
  const [openRealModal, setRealOpenModal] = useState(false);
  const [modalMinimize, setModalMinimize] = useState(false);
  const [open, setOpen] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [operatorStatusFilter, setOperatorStatusFilter] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchData = async (reset = false, opts = {}) => {
    if (page > totalPages && !reset) return;
    if (loading && !reset) return;

    if (reset && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (reset) setData([]);
    setLoading(true);
    try {
      const queryPage = reset ? 1 : page;
      let url = `/defect-inspection/v1/events?page=${queryPage}&limit=10`;

      if (opts.searchTerm && opts.searchTerm.trim().length > 0) {
        url += `&search=${encodeURIComponent(opts.searchTerm.trim())}`;
      }
      if (opts.selectedDate) {
        const sd = opts.selectedDate;
        const start = sd.startDate ? dayjs(sd.startDate) : (sd.format ? dayjs(sd) : null);
        const end = sd.endDate ? dayjs(sd.endDate) : (sd.format ? dayjs(sd) : null);

        if (start && end && start.isValid() && end.isValid()) {
          if (start.isSame(end, "day")) {
            url += `&filter_created_date:eq=${start.format("YYYY-MM-DD")}`;
          } else {
            url += `&filter_created_date:gte=${start.format("YYYY-MM-DD")}`;
            url += `&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
          }
        } else if (start && start.isValid()) {
          url += `&filter_created_date:gte=${start.format("YYYY-MM-DD")}`;
        } else if (end && end.isValid()) {
          url += `&filter_created_date:lte=${end.format("YYYY-MM-DD")}`;
        }
      }
      if (opts.deviceFilter) {
        url += `&filter_device_name:eq=${encodeURIComponent(opts.deviceFilter)}`;
      }
      if (opts.statusFilter) {
        url += `&filter_status:eq=${encodeURIComponent(opts.statusFilter)}`;
      }
      if (opts.operatorStatusFilter) {
        url += `&filter_operator_status:eq=${encodeURIComponent(opts.operatorStatusFilter)}`;
      }

      const response = await axiosPrivate.get(url, {
        signal: controller.signal,
      });

      setAPIEventsData(response?.data);
      if (reset || queryPage === 1) {
        setData(response?.data?.events || []);
        setPage(1);
      } else {
        setData((prevData) => [
          ...(prevData || []),
          ...(response?.data?.events || []),
        ]);
      }

      setTotalPages(response?.data?.page_metadata?.total_pages || 1);
      setLoading(false);
    } catch (err) {
      if (err.name === "CanceledError" || err.message === "canceled") {
        console.log("Request canceled");
        return;
      }
      console.log(err);
      setLoading(false);
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsListView(true);
    setIsTableView(false);
  }, []);

  useEffect(() => {
    if (page > 1) {
      fetchData(false, { searchTerm, selectedDate, deviceFilter, statusFilter, operatorStatusFilter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

 useEffect(() => {
  if (!isTableView && isListView) {

    if (searchTerm.trim().length === 1) return; 

    const handler = setTimeout(() => {
      fetchData(true, { searchTerm, selectedDate, deviceFilter, statusFilter, operatorStatusFilter });
    }, 1400);

    return () => clearTimeout(handler);
  }
}, [searchTerm, selectedDate, deviceFilter, statusFilter, operatorStatusFilter, isTableView, isListView]);

  const firstCloseSkipRef = useRef(true);
  useEffect(() => {
    if (firstCloseSkipRef.current) {
      firstCloseSkipRef.current = false;
      return;
    }

    if (!openRealModal) {
      setPage(1);
      setData([]);
      fetchData(true, { searchTerm, selectedDate, deviceFilter, statusFilter, operatorStatusFilter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRealModal]);

  const handleIntersection = (entry) => {
    if (entry.isIntersecting && !loading && page < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const lastItemRef = useIntersectionObserver(handleIntersection, [
    loading,
    totalPages,
  ]);

  const eventsToShow = data || [];

  const deviceOptions = apiEventsData?.filter_metadata?.devices 
    ? Object.keys(apiEventsData.filter_metadata.devices)
    : [...new Set((eventsToShow || []).map((e) => e.device_display_name || e.device_name).filter(Boolean))];

  const handleListView = () => {
    setIsListView(true);
    setIsTableView(false);
  };

  const handleTableView = () => {
    setIsTableView(true);
    setIsListView(false);
  };

  const viewRealTime = () => {
    setRealOpenModal(true);
  };
  const viewRealTimeEvents = () => {};

  const getDefectiveColor = (event, accent = false) => {
    let side1_measurements = event?.side1_prediction?.measurements;
    let side2_measurements = event?.side2_prediction?.measurements;

    let isSide1DefectiveColor = side1_measurements?.some((element) => {
      return (
        Math.abs(element.predicted_length - element.length) >
        event?.side1_prediction?.threshold
      );
    });
    let isSide2DefectiveColor = side2_measurements?.some((element) => {
      return (
        Math.abs(element.predicted_length - element.length) >
        event?.side2_prediction?.threshold
      );
    });

    if (accent) {
      return isSide1DefectiveColor || isSide2DefectiveColor
        ? EVENT_COLORS.DEFECT_SECONDARY_COLOR
        : EVENT_COLORS.WARN_SECONDARY_COLOR;
    } else {
      return isSide1DefectiveColor || isSide2DefectiveColor
        ? EVENT_COLORS.DEFECT_COLOR
        : EVENT_COLORS.WARN_COLOR;
    }
  };

  return (
    <div
      style={{
        paddingTop: "96px",
        height: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        padding: "96px 0 0 0", // Top padding handles AppBar, others handled by children
      }}
    >
      {!isHomePage && (
        <AppBarCommon
          name="Events"
          viewEvents={true}
          viewBtn={true}
          miniMize={modalMinimize}
          viewRealTime={viewRealTime}
          viewRealTimeEvents={viewRealTimeEvents}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          deviceFilter={deviceFilter}
          setDeviceFilter={setDeviceFilter}
          deviceOptions={deviceOptions}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          operatorStatusFilter={operatorStatusFilter}
          setOperatorStatusFilter={setOperatorStatusFilter}
          filterMetadata={apiEventsData?.filter_metadata}
          totalCount={apiEventsData?.page_metadata?.total_count || 0}
          loading={loading}
          sidebarWidth={drawerWidth}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          padding: "0px 26px 8px",
          boxSizing: "border-box",
        }}
      >
        <IconButton
          onClick={handleListView}
          sx={{ width: "50px", height: "50px" }}
        >
          <MenuIcon style={{ color: isListView ? "#239BB9" : "#9CE0E0" }} />
        </IconButton>
        <IconButton
          onClick={handleTableView}
          sx={{ width: "50px", height: "50px" }}
        >
          <TableChartIcon
            style={{ color: isTableView ? "#239BB9" : "#9CE0E0" }}
          />
        </IconButton>
      </div>
      {/* <div>
        <Button onClick={handleOpenModal}>Real events</Button>
      </div> */}
      <div
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          padding: "0 26px 26px",
          boxSizing: "border-box",
        }}
      >
        {isListView && eventsToShow && eventsToShow.length > 0 ? (
          <div
            style={{
              display: isListView ? "block" : "grid",
              gridTemplateColumns: isListView
                ? "none"
                : "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "20px",
              width: isListView ? "100%" : "100%",
              padding: isListView ? "0" : "20px",
              minHeight: "100vh",
              overflow: "visible",
            }}
          >
            {eventsToShow?.map((event, index) => (
              <EventCardItem
                key={event?.event_id || index}
                event={event}
                index={index}
                isLastItem={eventsToShow.length === index + 1}
                lastItemRef={lastItemRef}
                getDefectiveColor={getDefectiveColor}
                formatDateTime={formatDateTime}
              />
            ))}
          </div>
        ) : isTableView && eventsToShow && eventsToShow.length > 0 ? (
          <ViewEventsTable
            preloadData={apiEventsData}
            deviceFilter={deviceFilter}
            searchTerm={searchTerm}
            selectedDate={selectedDate}
            statusFilter={statusFilter}
            operatorStatusFilter={operatorStatusFilter}
          />
        ) : loading && (!data || data.length === 0) ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
              width: "100%",
            }}
          >
            <CircularProgress size={60} thickness={4} sx={{ color: "#316C7A" }} />
          </div>
        ) : !loading ? (
          <Typography
            style={{
              width: "100%",
              height: "80%",
              textAlign: "center",
              marginTop: "20px",
              fontWeight: "bold",
              color: "#115061",
              fontSize: "20px",
              fontFamily: "Work Sans",
            }}
          >
            No Data Available
          </Typography>
        ) : (
          <></>
        )}
        {loading && data && data.length > 0 && (
          <div style={{
              padding: "40px 20px",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              marginBottom: "40px"
            }}>
            <p style={{
                fontFamily: "Work Sans",
                fontSize: "18px",
                textAlign: "center",
                color: "#316C7A",
                fontWeight: "500",
                backgroundColor: "#F0F7F8",
                padding: "10px 30px",
                borderRadius: "25px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
              Loading more events....
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewEventsModal;
